import React from 'react';
import { HoverTooltip } from '../HoverTooltip';
import { ArrayFieldTemplateProps, ArrayFieldTemplateItemType } from '@rjsf/utils';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown, Delete } from '../ProjectIcons';
import { buttonClasses } from '../tailwindClassnames';

// A quarter smaller than the 30px default: in a list row it sits beside the
// move arrows and should not outweigh them. Shared with CustomArrayField.
export const LIST_DELETE_ICON_SIZE = 22.5;

// Template for rjsf-rendered arrays (arrays of objects — see generateUISchema's
// hasScalarItems gate; scalar arrays go through CustomArrayField instead).
// Lays the title and description full-width above the item list so nested
// arrays keep the whole card width instead of halving it at every level.
const CustomArrayFieldTemplate = (props: ArrayFieldTemplateProps) => {
    const { title, schema, items, canAdd, onAddClick, disabled, readonly, required } = props;
    const { t } = useTranslation();
    const showAdd = canAdd && !readonly;

    return (
        <div className="my-2 w-full">
            {/* The Add button sits beside the title and description, centred on
                them, the same way CustomArrayField places it for scalar lists. */}
            {(title || schema.description || showAdd) && (
                <div className="flex flex-row items-center justify-between gap-6 mb-2">
                    {/* Half the row for title and description, like every other
                        field (HorizontalFieldTemplate's 50/50 split). */}
                    <div className="flex flex-col w-1/2 min-w-0">
                        {title && (
                            <label className="font-semibold">
                                {title}
                                {required && <span className="ml-1 font-normal text-ui-status-error-destructive">*</span>}
                            </label>
                        )}
                        {schema.description && (
                            <span className="text-ui-text-primary-text text-sm mt-1">{schema.description}</span>
                        )}
                    </div>
                    {showAdd && (
                        <div className="w-1/2 min-w-0 flex justify-end">
                            <button
                                type="button"
                                className={`${buttonClasses.listAdd} shrink-0`}
                                disabled={disabled}
                                onClick={onAddClick}
                                data-testid="CustomArrayFieldTemplate-add-button"
                            >
                                {t('Add Item')}
                            </button>
                        </div>
                    )}
                </div>
            )}
            {items.map(({
                key,
                index,
                children,
                hasToolbar,
                hasMoveUp,
                hasMoveDown,
                hasRemove,
                onReorderClick,
                onDropIndexClick,
            }: ArrayFieldTemplateItemType) => (
                <div
                    key={key}
                    className="mb-3 w-full transition-all duration-300 flex flex-row items-center gap-2"
                    data-testid={`CustomArrayFieldTemplate-item-${index}`}
                >
                    <div className="flex-1 min-w-0">{children}</div>
                    {/* A flex sibling, not an absolute overlay on the fields, centred
                        on the item like every other control in these forms. Items
                        carry no border of their own: the list's divider frames them. */}
                    {hasToolbar && !readonly && (
                        <div className="flex flex-row items-center gap-1 shrink-0">
                            {(hasMoveUp || hasMoveDown) && (
                                <>
                                    <HoverTooltip content={t('Move up') || 'Move up'} position="top" focusable={false}>
                                        <button
                                            type="button"
                                            className="p-1 rounded hover:bg-greyscale-10 disabled:opacity-30 disabled:cursor-not-allowed"
                                            aria-label={t('Move up') || 'Move up'}
                                            disabled={disabled || !hasMoveUp}
                                            onClick={onReorderClick(index, index - 1)}
                                        >
                                            <ArrowUp />
                                        </button>
                                    </HoverTooltip>
                                    <HoverTooltip content={t('Move down') || 'Move down'} position="top" focusable={false}>
                                        <button
                                            type="button"
                                            className="p-1 rounded hover:bg-greyscale-10 disabled:opacity-30 disabled:cursor-not-allowed"
                                            aria-label={t('Move down') || 'Move down'}
                                            disabled={disabled || !hasMoveDown}
                                            onClick={onReorderClick(index, index + 1)}
                                        >
                                            <ArrowDown />
                                        </button>
                                    </HoverTooltip>
                                </>
                            )}
                            {hasRemove && (
                                <HoverTooltip content={t('Remove') || 'Remove'} position="top" focusable={false}>
                                    <button
                                        type="button"
                                        className="p-1 rounded hover:bg-greyscale-10 disabled:opacity-30 disabled:cursor-not-allowed"
                                        aria-label={t('Remove') || 'Remove'}
                                        disabled={disabled}
                                        onClick={onDropIndexClick(index)}
                                    >
                                        <Delete size={LIST_DELETE_ICON_SIZE} />
                                    </button>
                                </HoverTooltip>
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default CustomArrayFieldTemplate;
